import { useState } from "react";
import styled from "styled-components";
import { Link } from "react-router-dom";
import { cPath } from "../../constants";
import { storeSlugForCoupon } from "../../utils/storeSlugForCoupon";
import RichTextContent from "../ui/RichTextContent";

const Wrap = styled.div`
  margin-top: 1.5rem;
  background: hsl(${({ theme }) => theme.colors.card});
  border-radius: ${({ theme }) => theme.radius.lg};
  border: 1px solid hsl(${({ theme }) => theme.colors.border});
`;

const TabRow = styled.div`
  display: flex;
  border-bottom: 1px solid hsl(${({ theme }) => theme.colors.border});
`;

const TabBtn = styled.button`
  padding: 1rem 1.5rem;
  font-size: 0.875rem;
  font-weight: ${({ $active }) => ($active ? 600 : 500)};
  color: ${({ $active, theme }) =>
    $active
      ? `hsl(${theme.colors.foreground})`
      : `hsl(${theme.colors.mutedForeground})`};
  border-bottom: 2px solid
    ${({ $active, theme }) =>
      $active ? `hsl(${theme.colors.primary})` : "transparent"};
  margin-bottom: -1px;
  &:hover {
    color: hsl(${({ theme }) => theme.colors.foreground});
  }
`;

const Panel = styled.div`
  padding: 1.5rem;
`;

const P = styled.p`
  margin: 0 0 1rem;
  color: hsl(${({ theme }) => theme.colors.foreground});
  line-height: 1.6;
`;

const Ul = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
`;

const Li = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: hsl(${({ theme }) => theme.colors.mutedForeground});
  margin-bottom: 0.375rem;
`;

const Comment = styled.div`
  border-bottom: 1px solid hsl(${({ theme }) => theme.colors.border});
  padding-bottom: 1rem;
  margin-bottom: 1rem;
  &:last-child {
    border-bottom: none;
    margin-bottom: 0;
    padding-bottom: 0;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 5rem;
  padding: 0.75rem;
  border-radius: ${({ theme }) => theme.radius.md};
  border: 1px solid hsl(${({ theme }) => theme.colors.border});
  font-size: 0.875rem;
  resize: vertical;
  margin-bottom: 0.75rem;
`;

const SubmitRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

const Btn = styled.button`
  padding: 0.5rem 1rem;
  border-radius: ${({ theme }) => theme.radius.md};
  font-weight: 600;
  font-size: 0.875rem;
  background: hsl(${({ theme }) => theme.colors.primary});
  color: hsl(${({ theme }) => theme.colors.primaryForeground});
  border: none;
  cursor: pointer;
  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const Muted = styled.p`
  margin: 0 0 1rem;
  font-size: 0.875rem;
  color: hsl(${({ theme }) => theme.colors.mutedForeground});
`;

const DelBtn = styled.button`
  font-size: 0.75rem;
  color: hsl(${({ theme }) => theme.colors.couponMaroon});
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  margin-left: 0.5rem;
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

function formatCommentTime(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return String(iso);
  }
}

export default function CouponTabsSection({
  coupon,
  activeTab,
  onTab,
  isAuthenticated = false,
  currentUserId,
  comments = [],
  commentsLoading = false,
  commentsSubmitting = false,
  onPostComment,
  onDeleteComment,
  mutatingCommentId,
}) {
  const [draft, setDraft] = useState("");
  const tabs = [
    { key: "details", label: "Deal Details" },
    { key: "info", label: "Product Info" },
    { key: "comments", label: `Comments (${coupon.comments ?? 0})` },
  ];

  const slug = storeSlugForCoupon(coupon);

  const submitComment = async () => {
    if (!draft.trim()) return;
    const ok = await onPostComment?.(draft);
    if (ok) setDraft("");
  };

  return (
    <Wrap>
      <TabRow>
        {tabs.map((t) => (
          <TabBtn
            key={t.key}
            type="button"
            $active={activeTab === t.key}
            onClick={() => onTab(t.key)}
          >
            {t.label}
          </TabBtn>
        ))}
      </TabRow>
      <Panel>
        {activeTab === "details" && (
          <>
            <P>{coupon.details}</P>
            {coupon.features?.length > 0 && (
              <>
                <p
                  style={{
                    fontWeight: 600,
                    margin: "1rem 0 0.5rem",
                    color: "hsl(220 40% 13%)",
                  }}
                >
                  Key Details:
                </p>
                <Ul>
                  {coupon.features.map((f, i) => (
                    <Li key={i}>
                      <span style={{ color: "hsl(43 80% 52%)" }}>•</span> {f}
                    </Li>
                  ))}
                </Ul>
              </>
            )}
            <P style={{ fontSize: "0.875rem", color: "hsl(220 10% 46%)" }}>
              Success rate:{" "}
              <strong style={{ color: "hsl(221 67% 44%)" }}>
                {coupon.successRate}%
              </strong>
              {" · "}Used:{" "}
              <strong style={{ color: "hsl(220 40% 13%)" }}>
                {coupon.usedCount.toLocaleString()} times
              </strong>
            </P>
          </>
        )}
        {activeTab === "info" && (
          <>
            <P as="div" style={{ margin: 0 }}>
              <RichTextContent html={coupon.description} />
            </P>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 16 }}>
              <img
                src={coupon.storeLogo}
                alt=""
                style={{ width: 32, height: 32, borderRadius: 4 }}
              />
              <div>
                <p style={{ margin: 0, fontWeight: 600, fontSize: "0.875rem" }}>
                  {coupon.storeName}
                </p>
                <Link
                  to={cPath(`store/${slug}`)}
                  style={{ fontSize: "0.75rem", color: "hsl(221 67% 44%)" }}
                >
                  View all {coupon.storeName} coupons →
                </Link>
              </div>
            </div>
          </>
        )}
        {activeTab === "comments" && (
          <>
            {commentsLoading && <Muted>Loading comments…</Muted>}
            {!commentsLoading && comments.length === 0 && (
              <Muted>No comments yet. Be the first to share your experience.</Muted>
            )}
            {comments.map((row) => {
              const initial = (row.userName || "U").slice(0, 1).toUpperCase();
              const mine = currentUserId && row.userId === currentUserId;
              return (
                <Comment key={row.id}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      marginBottom: 4,
                      flexWrap: "wrap",
                    }}
                  >
                    <div
                      style={{
                        width: 28,
                        height: 28,
                        borderRadius: "50%",
                        background: "hsl(221 67% 44% / 0.1)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "0.75rem",
                        fontWeight: 700,
                        color: "hsl(221 67% 44%)",
                      }}
                    >
                      {initial}
                    </div>
                    <span style={{ fontWeight: 600, fontSize: "0.875rem" }}>
                      {row.userName || "User"}
                    </span>
                    <span style={{ fontSize: "0.75rem", color: "hsl(220 10% 46%)" }}>
                      {formatCommentTime(row.createdAt)}
                    </span>
                    {mine ? (
                      <DelBtn
                        type="button"
                        disabled={commentsSubmitting && mutatingCommentId === row.id}
                        onClick={() => onDeleteComment?.(row.id)}
                      >
                        Delete
                      </DelBtn>
                    ) : null}
                  </div>
                  <p
                    style={{
                      margin: 0,
                      marginLeft: 36,
                      fontSize: "0.875rem",
                      color: "hsl(220 10% 46%)",
                    }}
                  >
                    {row.body}
                  </p>
                </Comment>
              );
            })}
            {isAuthenticated ? (
              <>
                <TextArea
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  placeholder="Share whether this deal worked for you…"
                  maxLength={4000}
                />
                <SubmitRow>
                  <Btn
                    type="button"
                    disabled={commentsSubmitting || !draft.trim()}
                    onClick={submitComment}
                  >
                    {commentsSubmitting ? "Posting…" : "Post comment"}
                  </Btn>
                </SubmitRow>
              </>
            ) : (
              <Muted>Sign in to post a comment on this deal.</Muted>
            )}
          </>
        )}
      </Panel>
    </Wrap>
  );
}
